import React, { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import CommandPalette from "../../components/CommandPalette";
import KeyboardShortcutsDialog from "../../components/KeyboardShortcutsDialog";
import FloatingAiButton from "../../components/FloatingAiButton";
import AIAdvisorDrawer from "../../components/AIAdvisorDrawer";
import { useAIAdvisorShortcut } from "../../hooks/useAIAdvisor";
import PageFAB, { PageActionsProvider } from "../../components/PageFAB";
import { EshopProvider } from "../../context/EshopContext";
import SettingsChangeNotifier from "../../components/SettingsChangeNotifier";
import ModuleErrorBoundary from "../../components/ModuleErrorBoundary";
import { BACKEND_URL, API_V1 } from "../../config/api";
import { BRAND, IS_GREENONE } from "../../config/brand";
import { WebSocketProvider } from "../../contexts/WebSocketContext";

import {
  SPACING_DENSITY, getSpacingDensity, setSpacingDensity, getVisibleNav, PERMANENT_LOGO,
} from "./layout/navConfig";
import { useAdminPrefs } from "./layout/useAdminPrefs";
import { Sidebar, MobileSidebarOverlay } from "./layout/Sidebar";
// iter413ap — Demo mode banner
import DemoBanner from "../../components/DemoBanner";
// iter413as — Guided tour for demo users (driver.js)
import DemoGuidedTour from "../../components/DemoGuidedTour";
import { TopNav } from "./layout/TopNav";
import { AdminTopBar } from "./layout/AdminTopBar";

const API = (BACKEND_URL || "") + "/api/v1";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // iter305: when /admin2 opens module routes inside windows, it appends ?embed=1.
  // In embed mode we skip sidebar/topbar/FAB so the page renders as bare content.
  // iter325: also treat any iframe context as embed mode — internal navigations
  // (e.g. Customers → CustomerDetail) drop the ?embed=1 query but the legacy
  // admin1 sidebar must still stay hidden when running inside an Admin2 window.
  const inIframe = typeof window !== "undefined" && window.self !== window.top;
  const embedMode = searchParams.get("embed") === "1" || inIframe;
  const location = useLocation();
  // iter353 — hide global topbar in PDF editor (it overlaps the editor toolbar).
  const isPdfEditor = (location?.pathname || "").startsWith("/admin/pdf-editor");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [branding, setBranding] = useState({ company_name: IS_GREENONE ? BRAND.productName : "Solar Green", company_logo: IS_GREENONE ? null : PERMANENT_LOGO });
  const { prefs, updatePrefs } = useAdminPrefs();
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const [spacingDensity, setSpacingDensityState] = useState(getSpacingDensity);
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [favoriteModules, setFavoriteModules] = useState([]);
  // iter324: Quick Capture moved to GlobalQuickCapture (works in admin1 + admin2 + mobile)

  // Load favorites from backend
  useEffect(() => {
    axios.get(`${API_V1}/user-prefs`, { withCredentials: true })
      .then(r => setFavoriteModules(r.data?.favorite_modules || []))
      .catch(() => {});
  }, []);

  const toggleFavorite = useCallback((moduleKey) => {
    setFavoriteModules(prev => {
      const next = prev.includes(moduleKey)
        ? prev.filter(m => m !== moduleKey)
        : [...prev, moduleKey];
      axios.put(`${API_V1}/user-prefs`, { favorite_modules: next }, { withCredentials: true }).catch(() => {});
      return next;
    });
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    let gPressed = false;
    let gTimer = null;

    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      const isInput = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target.isContentEditable;

      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setCmdPaletteOpen(true);
        return;
      }

      if (isInput) return;

      if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      if (e.key === "g" && !e.ctrlKey && !e.metaKey) {
        gPressed = true;
        clearTimeout(gTimer);
        gTimer = setTimeout(() => { gPressed = false; }, 800);
        return;
      }
      if (gPressed) {
        gPressed = false;
        clearTimeout(gTimer);
        const routes = {
          d: "/admin/dashboard",
          c: "/admin/calendar",
          t: "/admin/tasks",
          p: "/admin/projects",
          z: "/admin/customers",
          n: "/admin/quotes",
        };
        if (routes[e.key]) {
          e.preventDefault();
          navigate(routes[e.key]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  const layout = prefs.layout || "sidebar";
  const navItems = getVisibleNav(user, prefs.visible_modules);
  const spacingPadding = (SPACING_DENSITY.find(s => s.key === spacingDensity) || SPACING_DENSITY[1]).padding;

  // iter413g — Global AI Radce drawer (Cmd+I / Ctrl+I + top-bar trigger)
  const advisor = useAIAdvisorShortcut();

  const handleSpacingChange = (key) => {
    setSpacingDensityState(key);
    setSpacingDensity(key);
  };

  useEffect(() => {
    axios.get(`${API}/public/branding`).then(r => {
      if (r.data?.company_name || r.data?.company_logo) {
        // GreenONE V3 — product brand overrides company branding for the app
        // chrome (header/logo). The legal company_name stays in DB for documents.
        const b = { ...r.data };
        if (IS_GREENONE) { b.company_name = BRAND.productName; b.company_logo = null; }
        setBranding(b);
      }
    }).catch(() => {});
    // Load and apply design theme
    import("../../components/admin/DesignSettings").then(mod => {
      axios.get(`${API}/admin/settings`).then(r => {
        mod.applyDesignTheme(r.data?.design || {});
      }).catch(() => {
        mod.applyDesignTheme({});
      });
    });
  }, []);

  // iter305 embed mode short-circuit — render bare page content so it can be
  // shown inside an Admin 2 window without the chrome (sidebar/topbar/FAB).
  // Placed AFTER all hooks so React rules-of-hooks aren't violated.
  if (embedMode) {
    return (
      <WebSocketProvider userId={user?.user_id}>
        <EshopProvider>
        <PageActionsProvider>
          {/* iter413af — Embed mode (admin2 window) gets compact padding and a
              CSS variable `--section-gap: 0.5rem` to drastically shrink the
              vertical whitespace between page header, tabs, stats toggle, filter
              bar and the table. Saves ~120-200px per module page so the data
              table fills the window viewport instead of leaving an empty band
              between filters and rows.
              Also: hides the page-level H1 title via [data-testid="quotes-module"] > .flex
              first-child since admin2 window already shows the module name in
              its titlebar. We keep the action buttons (Export/Import/Nová...). */}
          <style>{`
            [data-testid="admin-embed-mode"] {
              --section-gap: 0.5rem;
            }
            [data-testid="admin-embed-mode"] [data-testid="stats-toggle-wrapper"] > div:first-child {
              padding-top: 0.25rem;
              padding-bottom: 0.25rem;
            }
          `}</style>
          <div className={`h-screen w-screen overflow-y-auto bg-slate-50 ${spacingPadding}`}
               data-testid="admin-embed-mode"
               style={{ paddingTop: "0.75rem", paddingBottom: "0.75rem" }}>
            <div className="animate-fade-in">
              <ModuleErrorBoundary routeKey={location.pathname}><Outlet /></ModuleErrorBoundary>
            </div>
          </div>
        </PageActionsProvider>
        </EshopProvider>
      </WebSocketProvider>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const sidebarProps = {
    user, branding, layout, prefs, favoriteModules, toggleFavorite,
    onClose: () => setSidebarOpen(false),
    onLogout: handleLogout,
    onOpenSearch: () => setCmdPaletteOpen(true),
    // iter413r — Quick toggle: collapse to icons-only / expand back
    onToggleCompact: () => updatePrefs({ layout: layout === "compact" ? "sidebar" : "compact" }),
  };

  // ============ SIDEBAR / COMPACT LAYOUT ============
  if (layout === "sidebar" || layout === "compact") {
    const sidebarW = layout === "compact" ? "w-16" : "w-56";

    return (
      <WebSocketProvider userId={user?.user_id}>
        <EshopProvider>
        <PageActionsProvider>
        <div className="flex h-screen bg-slate-50 overflow-hidden">
          {/* Desktop sidebar */}
          <aside className={`hidden lg:flex ${sidebarW} bg-slate-900 flex-col flex-shrink-0 transition-all duration-300 ease-in-out`}>
            <Sidebar {...sidebarProps} />
          </aside>

          {/* Mobile sidebar */}
          {sidebarOpen && (
            <MobileSidebarOverlay onClose={() => setSidebarOpen(false)}>
              <Sidebar {...sidebarProps} isMobile />
            </MobileSidebarOverlay>
          )}

          {/* Main */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {!isPdfEditor && (
              <AdminTopBar
                onToggleSidebar={() => setSidebarOpen(true)}
                layout={layout}
                onLayoutChange={l => updatePrefs({ layout: l })}
                showLayoutPicker={showLayoutPicker}
                setShowLayoutPicker={setShowLayoutPicker}
                isDark={isDark}
                toggleTheme={toggleTheme}
                spacingDensity={spacingDensity}
                onSpacingChange={handleSpacingChange}
                onOpenSearch={() => setCmdPaletteOpen(true)}
              />
            )}
            <main className={`flex-1 overflow-y-auto ${isPdfEditor ? '' : spacingPadding}`}>
              <div className="animate-fade-in">
                <DemoBanner />
                <DemoGuidedTour />
                <ModuleErrorBoundary routeKey={location.pathname}><Outlet /></ModuleErrorBoundary>
              </div>
            </main>
          </div>

          <CommandPalette isOpen={cmdPaletteOpen} onClose={() => setCmdPaletteOpen(false)} />
          <KeyboardShortcutsDialog isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
          {/* iter395 — FloatingAiButton odstraněn: kolidoval s GlobalQuickCapture na stejné pozici (right-6 bottom-6). AI chat je nyní v topbaru. */}
          <PageFAB />
          <SettingsChangeNotifier />
          <AIAdvisorDrawer open={advisor.open} onClose={advisor.close} initialPageContext={advisor.pageContext} />
        </div>
        </PageActionsProvider>
        </EshopProvider>
      </WebSocketProvider>
    );
  }

  // ============ TOP NAV LAYOUT ============
  return (
    <WebSocketProvider userId={user?.user_id}>
      <PageActionsProvider>
      <div className="flex flex-col h-screen bg-slate-50 overflow-hidden">
        <TopNav
          user={user}
          branding={branding}
          navItems={navItems}
          layout={layout}
          onLayoutChange={l => updatePrefs({ layout: l })}
          showLayoutPicker={showLayoutPicker}
          setShowLayoutPicker={setShowLayoutPicker}
          isDark={isDark}
          toggleTheme={toggleTheme}
          spacingDensity={spacingDensity}
          onSpacingChange={handleSpacingChange}
          onOpenSearch={() => setCmdPaletteOpen(true)}
          onLogout={handleLogout}
        />
        <main className={`flex-1 overflow-y-auto ${spacingPadding}`}>
          <div className="animate-fade-in">
            <DemoBanner />
            <DemoGuidedTour />
            <ModuleErrorBoundary routeKey={location.pathname}><Outlet /></ModuleErrorBoundary>
          </div>
        </main>

        <CommandPalette isOpen={cmdPaletteOpen} onClose={() => setCmdPaletteOpen(false)} />
        <KeyboardShortcutsDialog isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
        {/* iter395 — FloatingAiButton odstraněn: kolidoval s GlobalQuickCapture (oba na right-6 bottom-6). AI chat je nyní v topbaru / kebab menu. */}
        <PageFAB />
        <SettingsChangeNotifier />
        <AIAdvisorDrawer open={advisor.open} onClose={advisor.close} initialPageContext={advisor.pageContext} />
      </div>
      </PageActionsProvider>
    </WebSocketProvider>
  );
}
